import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import Grid from '@material-ui/core/Grid';
import ListSubheader from '@material-ui/core/ListSubheader';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import convertAddressToUpsert from '@afosto/utils/convertAddressToUpsert';
import convertContactToUpsert from '@afosto/utils/convertContactToUpsert';
import convertOrganisationToUpsert from '@afosto/utils/convertOrganisationToUpsert';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import Address from '../Address';
import AddressForm from '../AddressForm';
import ContentDrawer from '../ContentDrawer';
import translations from './translations';
import styled from 'styled-components';
import clsx from 'clsx';

const AddressContainer = styled(CardActionArea)`
  && {
    border: 1px solid #ccc;
    border-radius: ${props => props.theme.spacing(0.5)}px;
    padding: ${props => props.theme.spacing(2)}px;

    &.active {
      border: 1px solid ${props => props.theme.palette.primary.main};
    }
  }
`;

const AddressSelectionDrawer = ({
  isOpen,
  onClose,
  contact,
  onCreateContactAddress,
  organisation,
  onCreateOrganisationAddress,
  selectedAddress,
  setSelectedAddress,
  showSetAsBillingAddress,
  showSetAsShippingAddress,
  type,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showContactAddressForm, setShowContactAddressForm] = useState(false);
  const [showOrganisationAddressForm, setShowOrganisationAddressForm] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const { id: selectedAddressId } = selectedAddress ?? {};

  const contactAddresses = [
    ...(contact?.[type]?.primary ? [contact[type].primary] : []),
    ...(contact?.[type]?.secondary ? contact[type].secondary : []),
  ];

  const organisationAddresses = [
    ...(organisation?.[type]?.primary ? [organisation[type].primary] : []),
    ...(organisation?.[type]?.secondary ? organisation[type].secondary : []),
  ];

  const addressesRef = useRef({
    contact: contactAddresses,
    organisation: organisationAddresses,
  });
  const selectedAddressRef = useRef(selectedAddress);
  const { primary: primaryOrganisation = {} } = contact?.organisations ?? {};
  const prefilledAddress = {
    given_name: contact?.given_name,
    additional_name: contact?.additional_name,
    family_name: contact?.family_name,
    organisation: organisation ? organisation?.name : primaryOrganisation?.name,
  };

  useEffect(() => {
    addressesRef.current = {
      contact: contactAddresses,
      organisation: organisationAddresses,
    };
    selectedAddressRef.current = selectedAddress;
  });

  useEffect(() => {
    const { current: availableAddresses } = addressesRef;
    const addressIds = [
      ...availableAddresses.contact.map(address => address?.id),
      ...availableAddresses.organisation.map(address => address?.id),
    ];

    if (selectedAddressId && !addressIds.includes(selectedAddressId)) {
      const { current: currentSelectedAddress } = selectedAddressRef;

      if (currentSelectedAddress) {
        setAddresses([currentSelectedAddress]);
      }
    }
  }, [selectedAddressId]);

  useEffect(() => {
    if (!isOpen && showAddressForm) {
      setShowAddressForm(false);
    }
  }, [isOpen, showAddressForm]);

  /**
   * Handle create a new contact address
   * @param {object} addressFormData
   * @returns {Promise<never>}
   */
  const handleCreateContactAddress = async addressFormData => {
    try {
      const {
        isPrimary,
        setAsShippingAddress,
        setAsBillingAddress,
        ...addressData
      } = addressFormData;
      const upsertAddress = convertAddressToUpsert(addressData);
      const response = await addressesApi.createAddress(upsertAddress);
      const { data } = response;

      const latestContactResponse = await contactsApi.getContact(contact.id);
      const { data: latestContact } = latestContactResponse;

      const upsertedContact = convertContactToUpsert(latestContact);

      if (isPrimary || !upsertedContact[type].primary) {
        upsertedContact[type].secondary = [
          ...upsertedContact[type].secondary,
          ...(upsertedContact[type].primary ? [upsertedContact[type].primary] : []),
        ];

        upsertedContact[type].primary = data.id;
      } else {
        const currentSecondaryAddresses = (upsertedContact[type]?.secondary ?? []).filter(
          id => id !== data.id,
        );
        upsertedContact[type].secondary = [...currentSecondaryAddresses, data.id];
      }

      if (setAsBillingAddress || setAsShippingAddress) {
        const secondType = setAsShippingAddress ? 'shipping' : 'billing';

        if (!upsertedContact[secondType].primary) {
          upsertedContact[secondType].primary = data.id;
        } else {
          upsertedContact[secondType].secondary = [
            ...upsertedContact[secondType].secondary,
            data.id,
          ];
        }
      }

      const updatedContactResponse = await contactsApi.upsertContact(upsertedContact);
      const { data: updatedContact } = updatedContactResponse;

      onCreateContactAddress(data, updatedContact);

      enqueueSnackbar(intl.formatMessage(translations.feedback.addressCreated), {
        variant: 'success',
      });

      setShowContactAddressForm(false);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.addressNotCreated), {
        variant: 'error',
      });
    }
  };

  /**
   * Handle create a new organisation address
   * @param {object} addressFormData
   * @returns {Promise<never>}
   */
  const handleCreateOrganisationAddress = async addressFormData => {
    try {
      const {
        isPrimary,
        setAsShippingAddress,
        setAsBillingAddress,
        ...addressData
      } = addressFormData;
      const upsertAddress = convertAddressToUpsert(addressData);
      const response = await addressesApi.createAddress(upsertAddress);
      const { data } = response;

      const organisationResponse = await organisationsApi.viewOrganisation(organisation?.id);
      const { data: latestOrganisation } = organisationResponse;

      const upsertedOrganisation = convertOrganisationToUpsert(latestOrganisation);

      if (isPrimary || !upsertedOrganisation[type].primary) {
        upsertedOrganisation[type].secondary = [
          ...upsertedOrganisation[type].secondary,
          ...(upsertedOrganisation[type].primary ? [upsertedOrganisation[type].primary] : []),
        ];

        upsertedOrganisation[type].primary = data.id;
      } else {
        const currentSecondaryAddresses = (upsertedOrganisation?.[type]?.secondary ?? []).filter(
          id => id !== data.id,
        );
        upsertedOrganisation[type].secondary = [...currentSecondaryAddresses, data.id];
      }

      if (setAsBillingAddress || setAsShippingAddress) {
        const secondType = setAsShippingAddress ? 'shipping' : 'billing';

        if (!upsertedOrganisation[secondType].primary) {
          upsertedOrganisation[secondType].primary = data.id;
        } else {
          upsertedOrganisation[secondType].secondary = [
            ...upsertedOrganisation[secondType].secondary,
            data.id,
          ];
        }
      }

      const updatedOrganisationResponse = await organisationsApi.updateOrganisation(
        organisation.id,
        upsertedOrganisation,
      );
      const { data: updatedOrganisation } = updatedOrganisationResponse;

      onCreateOrganisationAddress(data, updatedOrganisation);

      enqueueSnackbar(intl.formatMessage(translations.feedback.addressCreated), {
        variant: 'success',
      });

      setShowOrganisationAddressForm(false);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.addressNotCreated), {
        variant: 'error',
      });
    }
  };

  /**
   * Handle creating a new address.
   * @param {object} addressFormData
   * @returns {Promise<void>}
   */
  const handleCreateAddress = async addressFormData => {
    try {
      const { isPrimary, ...addressData } = addressFormData;
      const upsertAddress = convertAddressToUpsert(addressData);
      const response = await addressesApi.createAddress(upsertAddress);
      const { data } = response;

      setSelectedAddress(data, true);

      enqueueSnackbar(intl.formatMessage(translations.feedback.addressCreated), {
        variant: 'success',
      });

      setAddresses([...addresses, data]);
      setShowAddressForm(false);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.addressNotCreated), {
        variant: 'error',
      });
    }
  };

  /**
   * Render an address.
   * @param {object} address
   * @param {boolean} customAddress
   * @returns {*}
   */
  const renderAddress = (address, customAddress = false) => (
    <Grid item xs={12} key={address.id}>
      <Card>
        <AddressContainer
          onClick={() => setSelectedAddress(address, customAddress)}
          className={clsx({ active: selectedAddress?.id === address.id })}
        >
          <Address
            company={address.organisation}
            fullName={`${address.given_name} ${
              address.additional_name
                ? `${address.additional_name} ${address.family_name}`
                : `${address.family_name}`
            }`}
            addressLine1={address.address_line_1}
            addressLine2={address.address_line_2}
            postalCode={address.postal_code}
            city={address.locality}
            country={address.country_code}
            administrativeArea={address.administrative_area}
          />
        </AddressContainer>
      </Card>
    </Grid>
  );

  return (
    <ContentDrawer
      isOpen={isOpen}
      handleClose={onClose}
      drawerWidth="400px"
      title={
        showOrganisationAddressForm || showContactAddressForm || showAddressForm
          ? intl.formatMessage(translations.page.createAddress.title)
          : intl.formatMessage(translations.page.title)
      }
    >
      {!showContactAddressForm && !showOrganisationAddressForm && !showAddressForm && (
        <>
          {contact && (
            <>
              <ListSubheader disableGutters>
                <Typography variant="overline">
                  {intl.formatMessage(translations.page.groups.contact)}
                </Typography>
              </ListSubheader>
              <Grid container spacing={2}>
                {contactAddresses.map(contactAddress => renderAddress(contactAddress))}
              </Grid>
              <Box mt={2}>
                <Button onClick={() => setShowContactAddressForm(true)}>
                  {intl.formatMessage(translations.page.button.create)}
                </Button>
              </Box>
            </>
          )}
          {organisation && (
            <>
              <ListSubheader disableGutters>
                <Typography variant="overline">
                  {intl.formatMessage(translations.page.groups.organisation)}
                </Typography>
              </ListSubheader>
              <Grid container spacing={2}>
                {organisationAddresses.map(organisationAddress =>
                  renderAddress(organisationAddress),
                )}
              </Grid>
              <Box mt={2}>
                <Button onClick={() => setShowOrganisationAddressForm(true)}>
                  {intl.formatMessage(translations.page.button.create)}
                </Button>
              </Box>
            </>
          )}

          <>
            {(contact || organisation) && (
              <ListSubheader disableGutters>
                <Typography variant="overline">
                  {intl.formatMessage(translations.page.groups.other)}
                </Typography>
              </ListSubheader>
            )}
            <Grid container spacing={2}>
              {addresses.map(address => renderAddress(address, true))}
            </Grid>
            <Box mt={2}>
              <Button onClick={() => setShowAddressForm(true)}>
                {intl.formatMessage(translations.page.button.create)}
              </Button>
            </Box>
          </>
        </>
      )}

      {showContactAddressForm && (
        <AddressForm
          address={prefilledAddress}
          onCancel={() => setShowContactAddressForm(false)}
          onSubmit={handleCreateContactAddress}
          showSetAsPrimary={contact[type].primary !== undefined && contact[type].primary !== null}
          showSetAsShippingAddress={showSetAsShippingAddress && type === 'billing'}
          showSetAsBillingAddress={showSetAsBillingAddress && type === 'shipping'}
        />
      )}

      {showOrganisationAddressForm && (
        <AddressForm
          address={prefilledAddress}
          onCancel={() => setShowOrganisationAddressForm(false)}
          onSubmit={handleCreateOrganisationAddress}
          showSetAsPrimary={
            organisation[type].primary !== undefined && organisation[type].primary !== null
          }
          showSetAsShippingAddress={showSetAsShippingAddress && type === 'billing'}
          showSetAsBillingAddress={showSetAsBillingAddress && type === 'shipping'}
        />
      )}

      {showAddressForm && (
        <AddressForm
          address={prefilledAddress}
          onCancel={() => setShowAddressForm(false)}
          onSubmit={handleCreateAddress}
          showSetAsPrimary={false}
        />
      )}
    </ContentDrawer>
  );
};

AddressSelectionDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  contact: PropTypes.object,
  onCreateContactAddress: PropTypes.func,
  organisation: PropTypes.object,
  onCreateOrganisationAddress: PropTypes.func,
  selectedAddress: PropTypes.object,
  setSelectedAddress: PropTypes.func,
  showSetAsBillingAddress: PropTypes.bool,
  showSetAsShippingAddress: PropTypes.bool,
  type: PropTypes.string,
};

AddressSelectionDrawer.defaultProps = {
  isOpen: false,
  onClose: undefined,
  contact: null,
  onCreateContactAddress: undefined,
  organisation: null,
  onCreateOrganisationAddress: undefined,
  selectedAddress: null,
  setSelectedAddress: undefined,
  showSetAsBillingAddress: true,
  showSetAsShippingAddress: true,
  type: 'billing',
};

export default AddressSelectionDrawer;
